import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import TextEditor from '@ui/components/TextEditor';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getFaqQuestionByIdAsync } from '@application/features/faqQuestion/faqQuestionThunk';
import { getFamilyFestFaqQuestionByIdAsync } from '@application/features/familyFestFaqQuestion/familyFestFaqQuestionThunk';
import { filterLanguagesByEventType } from '@application/helpers/language-filter-helper';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { FamilyFestFaqTypeEnum, faqTypeOfCategory } from '@application/enums/family-fest-faq-type-enum';
import { useFamilyFestFaqCategoryOptions } from '../../FaqTypeFilter';
const TranslationAccordion = ({ flagSrc, langCode, questionName, answerName, control, expanded, onExpandedChange }) => {
    const { t } = useTranslation();
    return (_jsxs(Accordion, { disableGutters: true, elevation: 0, expanded: expanded, onChange: (_, next) => onExpandedChange(next), sx: {
            borderRadius: 2,
            '&:before': { display: 'none' },
            overflow: 'hidden',
            boxShadow: 'none'
        }, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: 'text.primary' } }), sx: {
                    px: 2,
                    minHeight: 56,
                    '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        my: 1,
                        gap: 1
                    }
                }, children: [_jsx(Box, { component: 'img', src: flagSrc || `/assets/images/flags/${langCode.toLowerCase()}-flag.svg`, alt: langCode, width: 28, height: 'auto', sx: { flexShrink: 0 } }), _jsx(Typography, { variant: 'subtitle1', fontWeight: 600, sx: { flex: 1, minWidth: 0 }, children: langCode })] }), _jsxs(AccordionDetails, { sx: { pt: 0, px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: questionName, label: t('events.eventDetails.faqTab.dialog.question'), multiline: true, rows: 2 }), _jsx(Controller, { control: control, name: answerName, render: ({ field }) => (_jsx(TextEditor, { value: field.value, onChange: field.onChange, placeholder: t('events.eventDetails.faqTab.dialog.answer'), hideToolbar: true, showLinkTool: true, placeholderColor: 'rgba(0,0,0,0.6)' })) })] })] }));
};
const FaqsDetails = ({ faq, categoryName, visibleSection, onDeleteFaq, onSaveFaq, onBack }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const VdkEventForDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const isFamilyEvent = VdkEventForDetails?.type === EventTypeEnum.Family ? true : false;
    const allLanguagesState = useAppSelector((state) => state.languages.languages);
    const eventLanguagesState = useAppSelector((state) => state.languages.eventLanguages);
    const standardSelectedQuestion = useAppSelector((state) => state.faqQuestion.selectedQuestion);
    const familyFestSelectedQuestion = useAppSelector((state) => state.familyFestFaqQuestion.selectedFaqQuestion);
    const selectedQuestion = isFamilyEvent ? familyFestSelectedQuestion : standardSelectedQuestion;
    const standardCategories = useAppSelector((state) => state.faqCategory.categories);
    const familyFestCategories = useAppSelector((state) => state.familyFestFaqCategory.categories);
    const faqCategories = isFamilyEvent ? familyFestCategories : standardCategories;
    const [expandedLanguageId, setExpandedLanguageId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const allLanguages = allLanguagesState ?? [];
    const eventLanguagesRaw = (eventLanguagesState ?? []).slice();
    // Filter languages based on event type - exclude SV for non-FamilyFest events
    const filteredEventLanguagesRaw = filterLanguagesByEventType(eventLanguagesRaw, isFamilyEvent);
    const eventLanguages = filteredEventLanguagesRaw
        .map((eventLang) => {
        const globalLang = allLanguages.find((item) => item.id === eventLang.id);
        return globalLang
            ? {
                ...globalLang,
                ...eventLang,
                isPrimary: eventLang.isPrimary
            }
            : eventLang;
    })
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    const fallbackEn = allLanguages.find((l) => (l.code || '').toLowerCase() === 'en') ??
        {
            id: '',
            code: 'en',
            name: 'English',
            isPrimary: true
        };
    const primaryLanguage = eventLanguages.find((l) => l.isPrimary) ?? eventLanguages[0] ?? fallbackEn;
    const translationLanguages = eventLanguages.filter((l) => l.id && l.id !== primaryLanguage?.id);
    const translationLanguageIds = translationLanguages.map((l) => l.id).join('|');
    // FamilyFest categories carry a type (registration / event page); a question follows its category.
    const categoryFaqTypes = Object.fromEntries(faqCategories.map((c) => [
        c.id,
        faqTypeOfCategory(c)
    ]));
    const faqTypeOfCategoryId = (categoryId) => (categoryId && categoryFaqTypes[categoryId]) || FamilyFestFaqTypeEnum.Registration;
    const { control, reset, handleSubmit, watch, formState: { isDirty } } = useForm({
        defaultValues: {
            question: '',
            answer: '',
            categoryName: null,
            faqType: FamilyFestFaqTypeEnum.Registration,
            translations: {}
        }
    });
    const selectedCategoryId = watch('categoryName');
    // Fixed to the FAQ's own type (set on load); only categories of that type are offered.
    const faqType = watch('faqType');
    // FamilyFest: that type's categories come from the backend (get-by-type).
    const { options: familyFestCategoryOptions, isLoading: isLoadingCategories } = useFamilyFestFaqCategoryOptions(faqType, isFamilyEvent);
    const categoryOptions = isFamilyEvent
        ? familyFestCategoryOptions
        : faqCategories.map((c) => ({ label: c.name, value: c.id }));
    const lastResetSignatureRef = useRef('');
    useEffect(() => {
        if (!faq?.id)
            return;
        if (isFamilyEvent) {
            dispatch(getFamilyFestFaqQuestionByIdAsync(faq.id));
        }
        else {
            dispatch(getFaqQuestionByIdAsync({ id: faq.id }));
        }
    }, [dispatch, faq?.id, isFamilyEvent]);
    useEffect(() => {
        if (translationLanguages.length === 0) {
            if (expandedLanguageId !== null)
                setExpandedLanguageId(null);
            return;
        }
        const exists = translationLanguages.some((l) => l.id === expandedLanguageId);
        if (!exists) {
            setExpandedLanguageId(translationLanguages[0].id);
        }
    }, [translationLanguageIds, expandedLanguageId]);
    useEffect(() => {
        const questionTranslations = selectedQuestion?.translations ?? [];
        const primaryTranslation = questionTranslations.find((tr) => tr.languageId === primaryLanguage?.id);
        const translationMap = {};
        for (const lang of translationLanguages) {
            const found = questionTranslations.find((tr) => tr.languageId === lang.id);
            let questionValue = found?.question ?? '';
            let answerValue = found?.answer ?? '';
            translationMap[lang.id] = {
                question: questionValue,
                answer: answerValue
            };
        }
        const nextValues = {
            // base fields follow current primary language
            question: primaryTranslation?.question ?? selectedQuestion?.question ?? '',
            answer: primaryTranslation?.answer ?? selectedQuestion?.answer ?? '',
            categoryName: categoryName ?? null,
            faqType: faqTypeOfCategoryId(categoryName),
            translations: translationMap
        };
        const signature = JSON.stringify(nextValues);
        if (signature !== lastResetSignatureRef.current) {
            reset(nextValues);
            lastResetSignatureRef.current = signature;
        }
        // faqCategories: the FAQ's type is read from its category, which may load after the question.
    }, [selectedQuestion, categoryName, faqCategories, translationLanguageIds, primaryLanguage?.id, reset]);
    const handleReset = () => {
        const questionTranslations = selectedQuestion?.translations ?? [];
        const translationMap = {};
        for (const lang of translationLanguages) {
            const found = questionTranslations.find((tr) => tr.languageId === lang.id);
            translationMap[lang.id] = {
                question: found?.question ?? '',
                answer: found?.answer ?? ''
            };
        }
        const nextValues = {
            // keep base values as loaded for this opened details view
            question: selectedQuestion?.question ?? '',
            answer: selectedQuestion?.answer ?? '',
            categoryName: categoryName ?? null,
            faqType: faqTypeOfCategoryId(categoryName),
            translations: translationMap
        };
        const signature = JSON.stringify(nextValues);
        if (signature !== lastResetSignatureRef.current) {
            reset(nextValues);
            lastResetSignatureRef.current = signature;
        }
    };
    const onSubmit = (data) => {
        const questionId = selectedQuestion?.id ?? faq?.id;
        if (!questionId || !onSaveFaq)
            return;
        const translations = translationLanguages.map((lang) => ({
            languageId: lang.id,
            question: data.translations?.[lang.id]?.question?.trim() ?? '',
            answer: data.translations?.[lang.id]?.answer?.trim() ?? ''
        }));
        setIsSaving(true);
        onSaveFaq({
            id: questionId,
            question: data.question.trim(),
            answer: data.answer.trim(),
            sortOrder: selectedQuestion?.sortOrder ?? 1,
            faqCategoryId: data.categoryName ?? '',
            translations
        })
            .then((ok) => {
            if (ok) {
                reset(data);
                lastResetSignatureRef.current = JSON.stringify(data);
            }
        })
            .finally(() => setIsSaving(false));
    };
    const primaryCode = (primaryLanguage?.code || 'en').toUpperCase();
    const showDetails = !visibleSection || visibleSection === 'details';
    const showTranslations = !visibleSection || visibleSection === 'translations';
    return (_jsx(_Fragment, { children: _jsxs(Grid, { container: true, columnSpacing: 2, children: [showDetails && (_jsxs(Grid, { size: { xs: 12, md: translationLanguages.length > 0 && !visibleSection ? 6 : 12 }, children: [_jsx(Box, { pt: 2, children: _jsx(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: _jsx(Typography, { variant: 'h4', children: t('events.eventDetails.faqTab.faqDetailsLang', { lang: primaryCode }) }) }) }), _jsx(Box, { pt: '12px', children: _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'question', label: t('events.eventDetails.faqTab.dialog.question'), multiline: true, rows: 2 }) }), _jsx(Box, { pt: '12px', children: _jsx(Controller, { control: control, name: 'answer', render: ({ field }) => (_jsx(TextEditor, { value: field.value, onChange: field.onChange, placeholder: t('events.eventDetails.faqTab.dialog.answer'), hideToolbar: true })) }) }), _jsx(Box, { pt: '12px', children: _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'categoryName', label: t('events.eventDetails.faqTab.newCategoryDialog.categoryName'), multiple: false, options: categoryOptions, loading: isLoadingCategories }) }), _jsxs(Box, { display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: handleReset, disabled: isSaving || !isDirty, children: t('actions.reset') }), _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', onClick: handleSubmit(onSubmit), disabled: isSaving || !isDirty || !selectedCategoryId, children: t('actions.saveChanges') })] })] })), showTranslations && translationLanguages.length > 0 && (_jsx(Grid, { size: { xs: 12, md: !visibleSection ? 6 : 12 }, children: _jsxs(Box, { borderRadius: visibleSection ? 0 : 3, padding: '0px 16px 16px 16px', boxShadow: visibleSection ? 'none' : theme.customShadows.card, children: [!visibleSection && (_jsx(Box, { pt: 2, children: _jsx(Typography, { variant: 'subtitle2', children: t('events.eventDetails.faqTab.translations') }) })), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }, children: translationLanguages.map((lang) => (_jsx(TranslationAccordion, { flagSrc: lang.flagUrl, langCode: (lang.code || '').toUpperCase(), questionName: `translations.${lang.id}.question`, answerName: `translations.${lang.id}.answer`, control: control, expanded: expandedLanguageId === lang.id, onExpandedChange: (next) => setExpandedLanguageId(next ? lang.id : null) }, lang.id))) })] }) }))] }) }));
};
export default FaqsDetails;
